// src/utils/billCalculator.ts
import { Bill, Guest } from '../types';

const toCents = (amount: number) => Math.round(amount * 100);
const fromCents = (amountCents: number) => amountCents / 100;

// Convert the item's price into per-guest shares that sum back to the original total.
const getShareForGuestCents = (price: number, splitCount: number, guestIndex: number) => {
  if (splitCount === 0) return 0;
  const totalCents = toCents(price);
  const baseShare = Math.floor(totalCents / splitCount);
  const remainder = totalCents - (baseShare * splitCount);
  return guestIndex < remainder ? baseShare + 1 : baseShare;
};

const allocateByWeightInCents = (
  amountCents: number,
  guestIds: string[],
  weightByGuestId: Map<string, number>
): Map<string, number> => {
  const shares = new Map<string, number>(guestIds.map((id) => [id, 0]));
  if (guestIds.length === 0 || amountCents === 0) return shares;

  const totalWeight = guestIds.reduce((sum, id) => sum + (weightByGuestId.get(id) ?? 0), 0);

  if (totalWeight <= 0) {
    const baseShare = Math.floor(amountCents / guestIds.length);
    let remainder = amountCents - (baseShare * guestIds.length);
    guestIds.forEach((id) => {
      const value = baseShare + (remainder > 0 ? 1 : 0);
      shares.set(id, value);
      if (remainder > 0) remainder -= 1;
    });
    return shares;
  }

  const remainders = guestIds.map((id, index) => {
    const weight = weightByGuestId.get(id) ?? 0;
    const scaled = amountCents * weight;
    const floorShare = Math.floor(scaled / totalWeight);
    shares.set(id, floorShare);
    return { id, index, remainder: scaled % totalWeight };
  });

  let remainderCents = amountCents - guestIds.reduce((sum, id) => sum + (shares.get(id) ?? 0), 0);
  remainders
    .sort((a, b) => {
      if (b.remainder !== a.remainder) return b.remainder - a.remainder;
      return a.index - b.index;
    })
    .slice(0, remainderCents)
    .forEach(({ id }) => {
      shares.set(id, (shares.get(id) ?? 0) + 1);
      remainderCents -= 1;
    });

  return shares;
};

const currencyLocaleMap: Record<string, string> = {
  USD: 'en-US',
  CAD: 'en-CA',
  EUR: 'de-DE',
  GBP: 'en-GB',
  AUD: 'en-AU',
  JPY: 'ja-JP'
};

export const calculateBreakdown = (guest: Guest, bill: Bill) => {
  let guestSubtotalCents = 0;
  const guestItems: { name: string; amount: number }[] = [];
  const guestIds = bill.guests.length > 0 ? bill.guests.map((entry) => entry.id) : [guest.id];
  const subtotalByGuestId = new Map<string, number>(guestIds.map((id) => [id, 0]));

  // 1. Sum up specific items
  for (const item of bill.items) {
    const splitCount = item.assignedGuestIds.length;
    if (!splitCount) continue;
    for (let index = 0; index < item.assignedGuestIds.length; index += 1) {
      const assignedGuestId = item.assignedGuestIds[index];
      const shareCents = getShareForGuestCents(item.price, splitCount, index);
      subtotalByGuestId.set(assignedGuestId, (subtotalByGuestId.get(assignedGuestId) ?? 0) + shareCents);
      if (assignedGuestId === guest.id) {
        guestSubtotalCents += shareCents;
        const displayName = splitCount > 1
          ? `${item.name} (1/${splitCount})`
          : item.name;
        guestItems.push({ name: displayName, amount: fromCents(shareCents) });
      }
    }
  }

  // 2. Helper to calculate charge values in cents
  const getChargeCents = (amount: number, isPercent: boolean) =>
    isPercent
      ? Math.round((bill.subtotal * (amount / 100)) * 100)
      : toCents(amount);

  const taxCents = getChargeCents(bill.taxInput, bill.taxIsPercentage);
  const serviceCents = getChargeCents(bill.serviceChargeInput, bill.serviceIsPercentage);
  const autoGratCents = getChargeCents(bill.autoGratuityInput, bill.autoGratIsPercentage);
  const addGratCents = getChargeCents(bill.additionalGratuityInput, bill.addGratIsPercentage);

  const taxShares = allocateByWeightInCents(taxCents, guestIds, subtotalByGuestId);
  const serviceShares = allocateByWeightInCents(serviceCents, guestIds, subtotalByGuestId);
  const autoGratShares = allocateByWeightInCents(autoGratCents, guestIds, subtotalByGuestId);
  const addGratShares = allocateByWeightInCents(addGratCents, guestIds, subtotalByGuestId);

  const taxShareCents = taxShares.get(guest.id) ?? 0;
  const serviceShareCents = serviceShares.get(guest.id) ?? 0;
  const autoGratuityShareCents = autoGratShares.get(guest.id) ?? 0;
  const additionalGratuityShareCents = addGratShares.get(guest.id) ?? 0;
  const totalCents =
    guestSubtotalCents +
    taxShareCents +
    serviceShareCents +
    autoGratuityShareCents +
    additionalGratuityShareCents;

  return {
    subtotal: fromCents(guestSubtotalCents),
    taxShare: fromCents(taxShareCents),
    serviceShare: fromCents(serviceShareCents),
    autoGratuityShare: fromCents(autoGratuityShareCents),
    additionalGratuityShare: fromCents(additionalGratuityShareCents),
    total: fromCents(totalCents),
    items: guestItems
  };
};

export const formatCurrency = (amount: number, currency: string = 'USD', locale?: string) => {
  const resolvedLocale = locale ?? currencyLocaleMap[currency] ?? 'en-US';
  return new Intl.NumberFormat(resolvedLocale, { style: 'currency', currency }).format(amount);
};
